import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Observable, catchError, map, of } from 'rxjs';
import { HttpParams } from '@angular/common/http';
import { HttpService } from '../http/http.service';
import { environment } from '@env/environment';
import type {
  FiltrosConsultaErrores,
  LogError,
  RespuestaLogsErrores,
  PatronError,
  RegistroErrorRequest,
  RegistroErrorResponse,
  EstadisticasErrores,
  OpcionesExportacion,
  RespuestaExportacion,
} from '@core/interfaces/auditoria-errores.interface';

@Injectable({
  providedIn: 'root',
})
export class AuditoriaErroresService {
  private readonly httpService = inject(HttpService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly esNavegador = isPlatformBrowser(this.platformId);

  private readonly baseUrl = `${environment.endpoint}/auditoria`;
  private readonly erroresEndpointUrl = `${this.baseUrl}/errores`;

  public registrarError(request: RegistroErrorRequest): Observable<RegistroErrorResponse> {
    if (this.esErrorSobreEndpointErrores(request)) {
      return of(this.crearRegistroLocalFallback());
    }

    return this.httpService
      .doPost<RegistroErrorRequest, RegistroErrorResponse>(this.erroresEndpointUrl, request)
      .pipe(catchError(() => of(this.crearRegistroLocalFallback())));
  }

  public obtenerLogsErrores(filtros?: FiltrosConsultaErrores): Observable<RespuestaLogsErrores> {
    const params = this.construirParams(filtros);

    return this.httpService
      .doGet<RespuestaLogsErrores>(this.erroresEndpointUrl, { params })
      .pipe(catchError(() => of(this.crearRespuestaLogsVacia())));
  }

  public obtenerLogPorId(id: string): Observable<LogError | null> {
    const url = `${this.erroresEndpointUrl}/${id}`;

    return this.httpService.doGet<LogError>(url).pipe(catchError(() => of(null)));
  }

  public detectarPatrones(filtros?: FiltrosConsultaErrores): Observable<PatronError[]> {
    const url = `${this.erroresEndpointUrl}/patrones`;
    const params = this.construirParams(filtros);

    return this.httpService.doGet<{ patrones: PatronError[] }>(url, { params }).pipe(
      map((response) => response.patrones || []),
      catchError(() => of([])),
    );
  }

  public obtenerEstadisticas(filtros?: FiltrosConsultaErrores): Observable<EstadisticasErrores> {
    const url = `${this.erroresEndpointUrl}/estadisticas`;
    const params = this.construirParams(filtros);

    return this.httpService
      .doGet<EstadisticasErrores>(url, { params })
      .pipe(catchError(() => of(this.crearEstadisticasVacias())));
  }

  public exportarReporte(opciones: OpcionesExportacion): Observable<RespuestaExportacion | null> {
    const url = `${this.erroresEndpointUrl}/exportar`;

    return this.httpService
      .doPost<OpcionesExportacion, RespuestaExportacion>(url, opciones)
      .pipe(catchError(() => of(null)));
  }

  public descargarArchivo(archivoUrl: string, nombreArchivo: string): void {
    if (!this.esNavegador) {
      return;
    }

    if (!this.esUrlDescargaSegura(archivoUrl)) {
      return;
    }

    this.crearEnlaceTemporal(archivoUrl, nombreArchivo);
  }

  public descargarArchivoBase64(archivoBase64: string, nombreArchivo: string, tipoMime: string): void {
    if (!this.esNavegador) {
      return;
    }

    const blob = this.crearBlobDesdeBase64(archivoBase64, tipoMime);
    this.descargarBlob(blob, nombreArchivo);
  }

  private construirParams(filtros?: FiltrosConsultaErrores): HttpParams {
    if (!filtros) {
      return new HttpParams();
    }

    const transformadores: Partial<Record<keyof FiltrosConsultaErrores, (valor: unknown) => string>> = {
      pagina: (valor) => String(valor),
      tamanoPagina: (valor) => String(valor),
    };

    return (Object.entries(filtros) as Array<[keyof FiltrosConsultaErrores, unknown]>).reduce(
      (params, [clave, valor]) => {
        if (valor === undefined || valor === null || valor === '') {
          return params;
        }

        const transformador = transformadores[clave];
        const valorNormalizado = transformador ? transformador(valor) : String(valor);

        return params.set(clave, valorNormalizado);
      },
      new HttpParams(),
    );
  }

  private esErrorSobreEndpointErrores(request: RegistroErrorRequest): boolean {
    return request.urlSolicitud?.includes('/errores') ?? false;
  }

  private crearRegistroLocalFallback(): RegistroErrorResponse {
    return {
      id: 'error-local',
      timestamp: new Date().toISOString(),
      mensaje: 'Error no pudo ser registrado en auditoría',
    };
  }

  private crearRespuestaLogsVacia(): RespuestaLogsErrores {
    return {
      logs: [],
      paginacion: {
        paginaActual: 1,
        tamanoPagina: 50,
        totalRegistros: 0,
        totalPaginas: 0,
      },
    };
  }

  private crearEstadisticasVacias(): EstadisticasErrores {
    return {
      totalErrores: 0,
      erroresPorSeveridad: {
        exito: 0,
        advertencia: 0,
        error: 0,
        error_critico: 0,
      },
      erroresPorServicio: [],
      erroresPorDia: [],
      topErrores: [],
    };
  }

  private crearEnlaceTemporal(urlDescarga: string, nombreArchivo: string): void {
    const enlace = document.createElement('a');

    try {
      enlace.href = urlDescarga;
      enlace.download = nombreArchivo;
      enlace.style.visibility = 'hidden';
      document.body.appendChild(enlace);
      enlace.click();
    } finally {
      enlace.remove();
    }
  }

  private descargarBlob(blob: Blob, nombreArchivo: string): void {
    const urlTemporal = URL.createObjectURL(blob);

    try {
      this.crearEnlaceTemporal(urlTemporal, nombreArchivo);
    } finally {
      URL.revokeObjectURL(urlTemporal);
    }
  }

  private crearBlobDesdeBase64(archivoBase64: string, tipoMime: string): Blob {
    const byteCharacters = atob(archivoBase64);
    const byteNumbers = Uint8Array.from({ length: byteCharacters.length }, (_, index) =>
      byteCharacters.charCodeAt(index),
    );

    return new Blob([byteNumbers], { type: tipoMime });
  }

  private esUrlDescargaSegura(url: string): boolean {
    const urlMinuscula = url.trim().toLowerCase();
    return urlMinuscula.startsWith('https://') || urlMinuscula.startsWith('blob:');
  }
}
